/* eslint-disable react-refresh/only-export-components -- this context module intentionally exports its provider and hook */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

const THEME_STORAGE_KEY = 'theme';
const SYSTEM_PREFERENCE = 'system';
const VALID_THEMES = new Set(['light', 'dark']);
const VALID_PREFERENCES = new Set([...VALID_THEMES, SYSTEM_PREFERENCE]);
const DARK_MODE_QUERY = '(prefers-color-scheme: dark)';

const ThemeContext = createContext(null);
const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

const readStoredPreference = () => {
  if (typeof window === 'undefined') return 'light';

  try {
    const savedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
    return VALID_PREFERENCES.has(savedTheme) ? savedTheme : 'light';
  } catch {
    return 'light';
  }
};

const readSystemTheme = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }

  try {
    return window.matchMedia(DARK_MODE_QUERY).matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

const storePreference = (preference) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Theme selection still works for this session when storage is unavailable.
  }
};

const applyResolvedTheme = (resolvedTheme, preference) => {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolvedTheme);
  root.setAttribute('data-theme', resolvedTheme);
  root.setAttribute('data-theme-preference', preference);
  root.style.colorScheme = resolvedTheme;

  const themeColor = document.querySelector('meta[name="theme-color"]');
  themeColor?.setAttribute('content', resolvedTheme === 'light' ? '#f6f1e9' : '#080806');
};

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};

export const ThemeProvider = ({ children }) => {
  const [preference, setPreference] = useState(readStoredPreference);
  const [systemTheme, setSystemTheme] = useState(readSystemTheme);
  const resolvedTheme = preference === SYSTEM_PREFERENCE ? systemTheme : preference;

  useBrowserLayoutEffect(() => {
    applyResolvedTheme(resolvedTheme, preference);
    storePreference(preference);
  }, [preference, resolvedTheme]);

  useBrowserLayoutEffect(() => {
    if (preference !== SYSTEM_PREFERENCE || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    let mediaQuery;
    try {
      mediaQuery = window.matchMedia(DARK_MODE_QUERY);
    } catch {
      return undefined;
    }

    const updateSystemTheme = (event = mediaQuery) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    updateSystemTheme();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateSystemTheme);
      return () => mediaQuery.removeEventListener('change', updateSystemTheme);
    }

    // Older Safari versions expose MediaQueryList through addListener/removeListener.
    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(updateSystemTheme);
      return () => mediaQuery.removeListener(updateSystemTheme);
    }

    return undefined;
  }, [preference]);

  const setThemePreference = useCallback((nextPreference) => {
    if (!VALID_PREFERENCES.has(nextPreference)) return;

    if (nextPreference === SYSTEM_PREFERENCE) {
      setSystemTheme(readSystemTheme());
    }

    storePreference(nextPreference);
    setPreference(nextPreference);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemePreference(resolvedTheme === 'light' ? 'dark' : 'light');
  }, [resolvedTheme, setThemePreference]);

  const useSystemTheme = useCallback(() => {
    setThemePreference(SYSTEM_PREFERENCE);
  }, [setThemePreference]);

  const value = useMemo(() => ({
    // `theme` remains the resolved light/dark value for existing consumers.
    theme: resolvedTheme,
    resolvedTheme,
    preference,
    setThemePreference,
    toggleTheme,
    useSystemTheme,
  }), [preference, resolvedTheme, setThemePreference, toggleTheme, useSystemTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
